import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
  mcFile,
  mcFileArchive,
  mcFileAudio,
  mcFileCode,
  mcFileExcel,
  mcFileImage,
  mcFilePdf,
  mcFilePowerpoint,
  mcFileVideo,
  mcFileWord,
} from '@mc/react-ui-icons';

export const iconByMimeType = (type?: string): IconDefinition => {
  if (!type) {
    return mcFile;
  }

  if (type.startsWith('image')) {
    return mcFileImage;
  } else if (type.startsWith('audio')) {
    return mcFileAudio;
  } else if (type.startsWith('video')) {
    return mcFileVideo;
  } else {
    switch (type) {
      case 'application/gzip':
      case 'application/zip':
        return mcFileArchive;
      case 'application/pdf':
        return mcFilePdf;
      case 'application/msword':
      case 'application/vnd.:ms-word':
      case 'application/vnd.:oasis.opendocument.text':
      case 'application/vnd.:openxmlformats-officedocument.wordprocessingml':
        return mcFileWord;
      case 'application/vnd.:ms-excel':
      case 'application/vnd.:openxmlformats-officedocument.spreadsheetml':
      case 'application/vnd.:oasis.opendocument.spreadsheet':
        return mcFileExcel;
      case 'application/vnd.:ms-powerpoint':
      case 'application/vnd.:openxmlformats-officedocument.presentationml':
      case 'application/vnd.:oasis.opendocument.presentation':
        return mcFilePowerpoint;
      case 'text/html':
      case 'application/json':
        return mcFileCode;
      case 'text/plain':
      default:
        return mcFile;
    }
  }
};

const extensionToMimeType: { [key: string]: string } = {
  '.aac': 'audio/aac',
  '.abw': 'application/x-abiword',
  '.arc': 'application/x-freearc',
  '.avif': 'image/avif',
  '.avi': 'video/x-msvideo',
  '.azw': 'application/vnd.amazon.ebook',
  '.bin': 'application/octet-stream',
  '.bmp': 'image/bmp',
  '.bz': 'application/x-bzip',
  '.bz2': 'application/x-bzip2',
  '.cda': 'application/x-cdf',
  '.csh': 'application/x-csh',
  '.css': 'text/css',
  '.csv': 'text/csv',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.eot': 'application/vnd.ms-fontobject',
  '.epub': 'application/epub+zip',
  '.gz': 'application/gzip',
  '.gif': 'image/gif',
  '.htm': 'text/html',
  '.html ': 'text/html',
  '.ico': 'image/vnd.microsoft.icon',
  '.ics': 'text/calendar',
  '.jar': 'application/java-archive',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.js': 'text/javascript',
  '.json': 'application/json',
  '.jsonld': 'application/ld+json',
  '.mid': 'audio/midi',
  '.midi': 'audio/midi',
  '.mjs': 'text/javascript',
  '.mp3': 'audio/mpeg',
  '.mp4': 'video/mp4',
  '.mpeg': 'video/mpeg',
  '.mpkg': 'application/vnd.apple.installer+xml',
  '.odp': 'application/vnd.oasis.opendocument.presentation',
  '.ods': 'application/vnd.oasis.opendocument.spreadsheet',
  '.odt': 'application/vnd.oasis.opendocument.text',
  '.oga': 'audio/ogg',
  '.ogv': 'video/ogg',
  '.ogx': 'application/ogg',
  '.opus': 'audio/opus',
  '.otf': 'font/otf',
  '.png': 'image/png',
  '.pdf': 'application/pdf',
  '.php': 'application/x-httpd-php',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentati',
  '.rar': 'application/vnd.rar',
  '.rtf': 'application/rtf',
  '.sh': 'application/x-sh',
  '.svg': 'image/svg+xml',
  '.swf': 'application/x-shockwave-flash',
  '.tar': 'application/x-tar',
  '.tif ': 'image/tiff',
  '.tiff ': 'image/tiff',
  '.ts': 'video/mp2t',
  '.ttf': 'font/ttf',
  '.txt': 'text/plain',
  '.vsd': 'application/vnd.visio',
  '.wav': 'audio/wav',
  '.weba': 'audio/webm',
  '.webm': 'video/webm',
  '.webp': 'image/webp',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
  '.xhtml': 'application/xhtml+xml',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xml': 'application/xmlapplication/atom+xml, but application/xml',
  '.xul': 'application/vnd.mozilla.xul+xml',
  '.zip': 'application/zip',
  '.3gp': 'video/3gpp',
  '.3g2': 'video/3gpp2',
  '.7z': 'application/x-7z-compressed',
};

export const mimeTypeByFileName = (fileName?: string): string | undefined => {
  if (!fileName) {
    return undefined;
  }

  const n = fileName.lastIndexOf('.');
  if (n >= 0) {
    const extension = fileName.substr(n);
    return extensionToMimeType[extension];
  } else {
    return undefined;
  }
};

export const getFileNameSplit = (fileName?: string): string[] => {
  if (!fileName) {
    return ['', ''];
  }

  const n = fileName.lastIndexOf('.');
  const name = n >= 0 ? fileName.substring(0, n + 1) : fileName;
  const extension = n >= 0 ? fileName.substring(n + 1) : '';
  return [name, extension];
};
